import { Dispatch, FC, SetStateAction } from "react";
import { IIdmFeature, IIdmRole, IIdmStaffListItem, IIdmUserFeatureGrant } from "../../../entities/idm";
import { SearchItems } from "../../searchItems";
import { MyButton } from "../../../shared/ui/button";
import { ConfirmationAction } from "../../../shared/ui/confirmationAction";
import { Modal } from "../../../shared/ui/modal";
import { MyCheckbox } from "../../../shared/ui/myCheckbox/myCheckbox";
import { LoaderSpinner } from "../../../shared/ui/spinner";
import classes from './staffAccessModals.module.scss'

type TStaffSearchItem = IIdmStaffListItem & {name: string};
type TFeatureSearchItem = IIdmFeature & {name: string};

interface IRolesModalProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    roles: IIdmRole[];
    selectedIds: number[];
    onToggle: (roleId: number, selected: boolean) => void;
    onSave: () => void;
}

export const StaffRolesModal: FC<IRolesModalProps> = ({open, setOpen, roles, selectedIds, onToggle, onSave}) => (
    <Modal open={open} setOpen={setOpen}>
        <section className={classes.modal}>
            <h3 className={classes.title}>Назначить роли сотруднику</h3>
            <section className={classes.list}>
                {roles.map(role => (
                    <section key={role.id} className={classes.item}>
                        <MyCheckbox
                            checked={selectedIds.includes(role.id)}
                            onSelected={selected => onToggle(role.id, selected)}
                            label={`${role.name} (${role.code})`}
                        />
                    </section>
                ))}
            </section>
            <section className={classes.actions}><MyButton onClick={onSave}>Сохранить</MyButton></section>
        </section>
    </Modal>
)

interface IFeaturesModalProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    options: TFeatureSearchItem[];
    searchItems: TFeatureSearchItem[];
    setSearchItems: Dispatch<SetStateAction<TFeatureSearchItem[]>>;
    selectedIds: number[];
    isLoading: boolean;
    isGranted: (featureId: number) => boolean;
    onToggle: (featureId: number, selected: boolean) => void;
    onSave: () => void;
}

export const StaffFeaturesModal: FC<IFeaturesModalProps> = ({
    open, setOpen, options, searchItems, setSearchItems, selectedIds, isLoading, isGranted, onToggle, onSave,
}) => (
    <Modal open={open} setOpen={setOpen}>
        <section className={`${classes.modal} ${classes.wide}`}>
            <h3 className={classes.title}>Доступные фичи</h3>
            <section className={classes.search}>
                <SearchItems placeholder="Поиск по name, key, description" items={options} setItems={setSearchItems} />
            </section>
            <section className={classes.list}>
                {isLoading && <section className={classes.loader}><LoaderSpinner /></section>}
                {!isLoading && searchItems.length === 0 && <section className={classes.empty}>Нет доступных для выдачи фич</section>}
                {!isLoading && searchItems.map(feature => {
                    const granted = isGranted(feature.id)

                    return (
                        <section key={feature.id} className={classes.item}>
                            <MyCheckbox
                                checked={granted || selectedIds.includes(feature.id)}
                                onSelected={granted ? () => {} : selected => onToggle(feature.id, selected)}
                            >
                                <section className={classes.option}>
                                    <span>{feature.name} ({feature.key})</span>
                                    {granted && <span className={classes.granted}>Уже выдано</span>}
                                    <span className={classes.description}>{feature.description}</span>
                                </section>
                            </MyCheckbox>
                        </section>
                    )
                })}
            </section>
            <section className={classes.actions}><MyButton onClick={onSave}>Сохранить</MyButton></section>
        </section>
    </Modal>
)

interface IStaffRelationModalProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    title: string;
    emptyText: string;
    options: TStaffSearchItem[];
    searchItems: TStaffSearchItem[];
    setSearchItems: Dispatch<SetStateAction<TStaffSearchItem[]>>;
    selectedIds: number[];
    isLoading: boolean;
    error: string;
    onToggle: (userId: number, selected: boolean) => void;
    onSave: () => void;
    showRoles?: boolean;
}

export const StaffRelationModal: FC<IStaffRelationModalProps> = ({
    open, setOpen, title, emptyText, options, searchItems, setSearchItems,
    selectedIds, isLoading, error, onToggle, onSave, showRoles,
}) => (
    <Modal open={open} setOpen={setOpen}>
        <section className={classes.modal}>
            <h3 className={classes.title}>{title}</h3>
            <section className={classes.search}>
                <SearchItems placeholder="Поиск по ФИО, email или роли" items={options} setItems={setSearchItems} />
            </section>
            <section className={classes.list}>
                {isLoading && <section className={classes.loader}><LoaderSpinner /></section>}
                {!isLoading && searchItems.length === 0 && <section className={classes.empty}>{emptyText}</section>}
                {!isLoading && searchItems.map(item => (
                    <section key={item.user_id} className={classes.item}>
                        <MyCheckbox
                            checked={selectedIds.includes(item.user_id)}
                            onSelected={selected => onToggle(item.user_id, selected)}
                            label={`${item.full_name} (${item.email})${showRoles && item.roles.length ? ` • ${item.roles.join(', ')}` : ''}`}
                        />
                    </section>
                ))}
            </section>
            <section className={classes.actions}><MyButton onClick={onSave} error={error}>Сохранить</MyButton></section>
        </section>
    </Modal>
)

interface IFeatureTargetsModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    grant: IIdmUserFeatureGrant | null;
    options: TStaffSearchItem[];
    searchItems: TStaffSearchItem[];
    setSearchItems: Dispatch<SetStateAction<TStaffSearchItem[]>>;
    selectedIds: number[];
    isLoading: boolean;
    onToggle: (userId: number, selected: boolean) => void;
    onSave: () => void;
}

export const StaffFeatureTargetsModal: FC<IFeatureTargetsModalProps> = ({
    open, onOpenChange, grant, options, searchItems, setSearchItems,
    selectedIds, isLoading, onToggle, onSave,
}) => (
    <Modal open={open} setOpen={onOpenChange}>
        <section className={`${classes.modal} ${classes.wide}`}>
            <h3 className={classes.title}>Ограничения для фичи</h3>
            {grant && (
                <section className={classes.meta}>
                    <strong>{grant.feature_name}</strong>
                    <span>{grant.category} · {grant.feature_key}</span>
                    <span className={classes.description}>Выбери пользователей, к которым действует доступ. Пустой список означает глобальный доступ.</span>
                </section>
            )}
            <section className={classes.search}>
                <SearchItems placeholder="Поиск по ФИО, email или роли" items={options} setItems={setSearchItems} />
            </section>
            <section className={classes.list}>
                {searchItems.map(item => (
                    <section key={item.user_id} className={classes.item}>
                        <MyCheckbox checked={selectedIds.includes(item.user_id)} onSelected={selected => onToggle(item.user_id, selected)}>
                            <section className={classes.option}>
                                <span>{item.user_id}. {item.full_name}</span>
                                <span className={classes.metaText}>{item.email}</span>
                                <span className={classes.description}>{item.roles.join(', ') || 'Без ролей'}</span>
                            </section>
                        </MyCheckbox>
                    </section>
                ))}
            </section>
            <section className={classes.actions}><MyButton onClick={onSave}>{isLoading ? 'Сохраняем...' : 'Сохранить'}</MyButton></section>
        </section>
    </Modal>
)

interface IStaffConfirmationModalProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    title: string;
    onConfirm: () => void;
    type: 'send' | 'delete';
    isLoading: boolean;
}

export const StaffConfirmationModal: FC<IStaffConfirmationModalProps> = ({open, setOpen, title, onConfirm, type, isLoading}) => (
    <Modal open={open} setOpen={setOpen}>
        <ConfirmationAction title={title} setOpen={setOpen} onClick={onConfirm} type={type} isLoading={isLoading} />
    </Modal>
)
